import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  Divider,
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import Loading from '../components/commonComponents/Loading';

export default function AdminDirectory() {
  /* ===================== USERS ===================== */
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersOrderBy, setUsersOrderBy] = useState('name');
  const [usersOrder, setUsersOrder] = useState('asc');
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);

  /* ===================== CLUBS ===================== */
  const [clubs, setClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(true);
  const [clubsOrderBy, setClubsOrderBy] = useState('name');
  const [clubsOrder, setClubsOrder] = useState('asc');
  const [clubsPage, setClubsPage] = useState(0);
  const [clubsRowsPerPage, setClubsRowsPerPage] = useState(5);

  /* ===================== MEMBERSHIPS ===================== */
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(true);
  const [membershipsOrderBy, setMembershipsOrderBy] = useState('name');
  const [membershipsOrder, setMembershipsOrder] = useState('asc');
  const [membershipsPage, setMembershipsPage] = useState(0);
  const [membershipsRowsPerPage, setMembershipsRowsPerPage] = useState(10);

  /* ===================== FETCH DATA ===================== */
  useEffect(() => {
    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, 'users'));
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUsersLoading(false);
    };

    const fetchClubs = async () => {
      const snap = await getDocs(collection(db, 'clubs'));
      const filtered = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.explore !== true);
      setClubs(filtered);
      setClubsLoading(false);
    };

    const fetchMemberships = async () => {
      const snap = await getDocs(collection(db, 'memberships'));
      setMemberships(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setMembershipsLoading(false);
    };

    fetchUsers();
    fetchClubs();
    fetchMemberships();
  }, []);

  const sortData = (data, orderBy, order) =>
    [...data].sort((a, b) => {
      const aVal = a[orderBy] || '';
      const bVal = b[orderBy] || '';
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });

  if (usersLoading && clubsLoading && membershipsLoading) return <Loading />;

  /* ===================== SORTED DATA ===================== */
  const sortedUsers = sortData(users, usersOrderBy, usersOrder);
  const sortedClubs = sortData(clubs, clubsOrderBy, clubsOrder);
  const sortedMemberships = sortData(
    memberships,
    membershipsOrderBy,
    membershipsOrder
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* ===================== USERS ===================== */}
      <Typography variant="h4" gutterBottom>Users ({users.length}) </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {['name','email','role','schoolName','createdAt','uid'].map(col => (
                <TableCell key={col}>
                  <TableSortLabel
                    active={usersOrderBy === col}
                    direction={usersOrderBy === col ? usersOrder : 'asc'}
                    onClick={() => {
                      const isAsc = usersOrderBy === col && usersOrder === 'asc';
                      setUsersOrder(isAsc ? 'desc' : 'asc');
                      setUsersOrderBy(col);
                    }}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers
              .slice(usersPage * usersRowsPerPage, usersPage * usersRowsPerPage + usersRowsPerPage)
              .map(u => (
                <TableRow key={u.uid}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell>{u.schoolName}</TableCell>
                  <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>{u.uid}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={users.length}
          page={usersPage}
          rowsPerPage={usersRowsPerPage}
          onPageChange={(e,p)=>setUsersPage(p)}
          onRowsPerPageChange={e=>{setUsersRowsPerPage(+e.target.value);setUsersPage(0);}}
        />
      </TableContainer>

      <Divider sx={{ my: 6 }} />

      {/* ===================== CLUBS ===================== */}
      <Typography variant="h4" gutterBottom>Clubs ({clubs.length}) </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {['name','category','teacherName','admin','email','school','date','url'].map(col => (
                <TableCell key={col}>
                  <TableSortLabel
                    active={clubsOrderBy === col}
                    direction={clubsOrderBy === col ? clubsOrder : 'asc'}
                    onClick={() => {
                      const isAsc = clubsOrderBy === col && clubsOrder === 'asc';
                      setClubsOrder(isAsc ? 'desc' : 'asc');
                      setClubsOrderBy(col);
                    }}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Location</TableCell>
              <TableCell>Club ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedClubs
              .slice(clubsPage * clubsRowsPerPage, clubsPage * clubsRowsPerPage + clubsRowsPerPage)
              .map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.category}</TableCell>
                  <TableCell>{c.teacherName}</TableCell>
                  <TableCell>{c.admin}</TableCell>
                  <TableCell>{c.email}</TableCell>
                  <TableCell>{c.school || '-'}</TableCell>
                  <TableCell>{c.date?.toDate ? c.date.toDate().toLocaleString() : '-'}</TableCell>
                  <TableCell>{c.url ? <img src={c.url} alt="" width={50}/> : '-'}</TableCell>
                  <TableCell>
                    {[c.city,c.state,c.country,c.zipCode].filter(Boolean).join(', ') || '-'}
                  </TableCell>
                  <TableCell><code>{c.id}</code></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={clubs.length}
          page={clubsPage}
          rowsPerPage={clubsRowsPerPage}
          onPageChange={(e,p)=>setClubsPage(p)}
          onRowsPerPageChange={e=>{setClubsRowsPerPage(+e.target.value);setClubsPage(0);}}
        />
      </TableContainer>

      <Divider sx={{ my: 6 }} />

      {/* ===================== MEMBERSHIPS ===================== */}
      <Typography variant="h4" gutterBottom>Club Memberships ({memberships.length}) </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {['name','role','user','clubId','dateJoined'].map(col => (
                <TableCell key={col}>
                  <TableSortLabel
                    active={membershipsOrderBy === col}
                    direction={membershipsOrderBy === col ? membershipsOrder : 'asc'}
                    onClick={() => {
                      const isAsc = membershipsOrderBy === col && membershipsOrder === 'asc';
                      setMembershipsOrder(isAsc ? 'desc' : 'asc');
                      setMembershipsOrderBy(col);
                    }}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedMemberships
              .slice(
                membershipsPage * membershipsRowsPerPage,
                membershipsPage * membershipsRowsPerPage + membershipsRowsPerPage
              )
              .map(m => (
                <TableRow key={m.id}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.role}</TableCell>
                  <TableCell>{m.user}</TableCell>
                  <TableCell>{m.clubId}</TableCell>
                  <TableCell>
                    {m.dateJoined?.toDate ? m.dateJoined.toDate().toLocaleString() : '-'}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={memberships.length}
          page={membershipsPage}
          rowsPerPage={membershipsRowsPerPage}
          onPageChange={(e,p)=>setMembershipsPage(p)}
          onRowsPerPageChange={e=>{setMembershipsRowsPerPage(+e.target.value);setMembershipsPage(0);}}
        />
      </TableContainer>
    </Container>
  );
}
